import { Grid, Toolbar } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import { FeedStyleClasses, FormStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterResult } from 'components/AdvFilter';
import { CmdCourseItemDeleted, CmdWithFile, CmdWithImage, CmdWithVideo, CommandLabels } from "components/AdvFilter/CommonCommands";
import ButtonLink from "components/Buttons/ButtonLink";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import IconButtonFilter from 'components/Buttons/IconButtonFilter';
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { MuiFilterAppBar } from 'components/MuiExtras/Filter';
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { CourseItemStatus, FeedType, PublicationType } from "models/ApiEnums";
import { CourseItemOperationResult, FeedAttachType, FeedContentUpdate, FeedCourseListFilter, FeedItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import CourseAdminReorder from "views/Publication/Components/CourseAdminReorder";
import CardManager from "views/Timeline/Components/CardManager";

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    dialog: any;
    advFilters: AdvFilterResult[];
    inAdvFilter: boolean;
    postLastError: any;
}

export interface IProps {
    courseId: string;
    onGetReference?: (ref: FeedCourseItemsComponent) => void;
    onItemChanged?: (mustBePublished: boolean, changed: boolean) => void;
}

interface IDispatchProps {
    onReloadPublication: (uid: string, publicationId: string) => Promise<void>;
    onRemovePublication: (uid: string, publicationId: string) => Promise<CourseItemOperationResult>;
    onExpandPublication: (uid: string, publicationId: string) => Promise<void>;
    onFeedReset: () => void;
    onUpdateFeedData: (newItemDto: FeedContentUpdate) => void;
    onRemoveFromFeed: (uid: string, publicationId: string) => void;
    onGetFeed: (filter: FeedCourseListFilter, startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IStateProps & IDispatchProps & WithSnackbar & WithTranslation & WithStyles<typeof FormStyleClasses> & WithStyles<typeof FeedStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////

export class FeedCourseItemsComponent extends React.Component<Props, IState> {

    private filter: FeedCourseListFilter = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            postLastError: null,
            advFilters: [],
            inAdvFilter: false,
        };

        this.resetFilter(props);
    }


    ///
    private get commands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdWithVideo, displayName: cmdLabels[CmdWithVideo], group: 1 },
            { keyword: CmdWithImage, displayName: cmdLabels[CmdWithImage], group: 1 },
            { keyword: CmdWithFile, displayName: cmdLabels[CmdWithFile], group: 1 },
            { keyword: CmdCourseItemDeleted, displayName: cmdLabels[CmdCourseItemDeleted], group: 2 },
        ];
    }

    ///
    private resetFilter(props: Props) {
        this.filter = {
            courseId: props.courseId,
            searchText: "",
            attachTypes: [],
            showDeleted: false
        };
    }

    ///
    private resetFeed() {
        this.setState({ dialog: null, inAdvFilter: false }, () => this.props.onFeedReset());
    }

    ///
    private isFiltered() {
        return this.filter.searchText.length > 0 || this.filter.attachTypes.length > 0;
    }


    ///
    private handleReorder = () => {
        this.setState({
            dialog: <CourseAdminReorder
                courseId={this.props.courseId}
                onClose={() => this.resetFeed()}
            />
        });
    };

    private handleRemovePublication = (uid: string, publicationId: string, publicationType: PublicationType) => {
        return new Promise<boolean>((resolve) => {
            resolve(false);
        });
    };

    ///
    private handleBeforeDelete = (uid: string, item: FeedItemDto): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {

            const { editStatus } = item.publication.courseItem;

            const title = editStatus == CourseItemStatus.Active || editStatus == CourseItemStatus.MarkToInsert
                ? this.props.t("Common.Feed.Course.ConfirmationRemoveItemTitle")
                : this.props.t("Common.Feed.Course.ConfirmationUndeleteItemTitle");

            const message = editStatus == CourseItemStatus.Active || editStatus == CourseItemStatus.MarkToInsert
                ? this.props.t("Common.Feed.Course.ConfirmationRemoveItemMessage")
                : this.props.t("Common.Feed.Course.ConfirmationUndeleteItemMessage");

            const successMessage = editStatus == CourseItemStatus.Active || editStatus == CourseItemStatus.MarkToInsert
                ? this.props.t("Common.Feed.Messages.CourseRemoveItemSuccess")
                : this.props.t("Common.Feed.Messages.CourseUndeleteItemSuccess");

            this.setState({
                dialog: (
                    <MuiConfirmation
                        title={title}
                        message={message}
                        onClose={
                            (result) => {

                                this.setState({ dialog: null });

                                if (result) {
                                    this.props.onRemovePublication(uid, item.publicationId)
                                        .then(async (result) => {

                                            if (result.saved) {
                                                this.props.snackbar.showMessage(successMessage, "success");

                                                this.props.onItemChanged(result.mustBePublished, result.changed);

                                                await this.props.onReloadPublication(uid, item.publicationId);
                                            }
                                            else
                                                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");

                                            resolve(true);
                                        })
                                        .catch((error) => {
                                            this.setState({ dialog: null, postLastError: error });
                                            resolve(true);
                                        });
                                }
                                else {
                                    resolve(true);
                                }
                            }
                        }
                    />
                )
            });

        });
    };

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        if (searchText.length == 0 && advFilters.length == 0) {
            this.resetAdvFilter();
        }
        else {
            this.filter.searchText = searchText;

            this.filter.attachTypes = [];
            if (advFilters.findIndex(x => x.keyword == CmdWithVideo) > -1) this.filter.attachTypes.push(FeedAttachType.Video);
            if (advFilters.findIndex(x => x.keyword == CmdWithImage) > -1) this.filter.attachTypes.push(FeedAttachType.Image);
            if (advFilters.findIndex(x => x.keyword == CmdWithFile) > -1) this.filter.attachTypes.push(FeedAttachType.File);
            this.filter.showDeleted = advFilters.findIndex(x => x.keyword == CmdCourseItemDeleted) > -1;

            this.setState({ advFilters, inAdvFilter: false }, () => this.props.onFeedReset());
        }
    };

    ///
    private resetAdvFilter() {
        this.resetFilter(this.props);
        this.setState({ advFilters: [], inAdvFilter: false }, () => this.props.onFeedReset());
    }

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <AdvFilter
                id="advfilter"
                autoFocus
                commands={this.commands}
                selectedCommands={this.state.advFilters}
                inputValue={this.filter.searchText}
                placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                onEscape={() => this.setState({ inAdvFilter: false })}
                onUpdateSelection={this.handleUpdateFilter} />
        );
    }

    ///
    private renderBaseFilter(): JSX.Element {
        const { feed } = this.props;
        return (
            <Grid container justifyContent="space-between" alignItems="center">

                <Grid item>
                    <MuiColor variant={this.isFiltered() ? "warning" : "inherit"}>
                        <IconButtonFilter color="inherit" onClick={() => { this.setState({ inAdvFilter: true }); }} />
                        <span>
                            {
                                feed.totalRecords == 0
                                    ? this.props.t("Common.Feed.Course.Filter.Result_zero")
                                    : this.props.t("Common.Feed.Course.Filter.Result", { count: feed.totalRecords })
                            }
                        </span>
                        {
                            this.isFiltered()
                                ? <IconButtonCancel color="inherit" onClick={() => { this.resetAdvFilter(); }} />
                                : null
                        }
                    </MuiColor>
                </Grid>

            </Grid>
        );
    }

    ///
    private renderFilterBar(): JSX.Element {

        return (
            <MuiFilterAppBar position="static">
                <Toolbar variant="dense" disableGutters >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item xs>
                            {this.state.inAdvFilter && this.renderAdvFilter()}
                            {!this.state.inAdvFilter && this.renderBaseFilter()}
                        </Grid>
                        {
                            this.props.feed.totalRecords > 1 &&
                            <Grid item>
                                <ButtonLink
                                    text={this.props.t("Common.MenuAction.Reorder")}
                                    onClick={this.handleReorder}
                                />
                            </Grid>
                        }
                    </Grid>
                </Toolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    public componentDidMount() {
        if (this.props.onGetReference) this.props.onGetReference(this);
        this.feedReset();
    }

    ///
    public componentWillUnmount() {
        if (this.props.onGetReference) this.props.onGetReference(null);
        this.feedReset();
    }

    ///
    public render() {
        const { postLastError, dialog } = this.state;

        return (
            <>
                {dialog}

                <RequestErrorDialog
                    open={postLastError != null}
                    error={postLastError}
                    onClose={() => this.setState({ postLastError: null })}
                />


                {this.renderFilterBar()}

                <CardManager
                    classes={this.props.classes}
                    feedType={FeedType.CourseItems}
                    feed={this.props.feed}
                    snackbar={this.props.snackbar}
                    onGetFeed={(startIndex: number, endIndex: number) => this.props.onGetFeed(this.filter, startIndex, endIndex)}
                    onReloadPublication={this.props.onReloadPublication}
                    onBeforeDelete={this.handleBeforeDelete}
                    onRemovePublication={this.handleRemovePublication}
                    onExpandPublication={this.props.onExpandPublication}
                    onUpdatePublication={this.props.onUpdateFeedData}
                    onRemoveFromFeed={this.props.onRemoveFromFeed}
                />

            </>
        );
    }
}

const withAllStyles = withStyles(
    (theme) => ({
        ...FormStyleClasses(theme),
        ...FeedStyleClasses(theme)
    }),
    { withTheme: true },
);


function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feedPrivate
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.CourseListFeedAction(ownProps.courseId);

    return {
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onRemovePublication: (uid, publicationId) => dispatch(action.removeItemPublication(uid, publicationId)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onFeedReset: () => dispatch(action.feedReset()),
        onGetFeed: (filter: FeedCourseListFilter, startIndex: number, endIndex: number) => dispatch(action.getFeed(filter, startIndex, endIndex)),
    };
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withTranslation()(withAllStyles(withSnackbar(FeedCourseItemsComponent))));

